// Who the event happened to, kept on the Sentry scope.
//
// The scope is global and long-lived, so every write here has to be a full
// overwrite: a session that changes hotel chain, gets impersonated or logs out
// must never leave a previous session's identity attached to the next event.

import * as Sentry from '@sentry/react';

export interface SentryIdentity {
  // Effective (possibly impersonated) logged-in user. Never their email.
  userId: string | null;
  hotelChainId: string | null;
  roleId: number | null;
  isImpersonating: boolean;
  // Real actor behind an impersonation session, read from the access token's
  // subject — never inferred from `userId`, which the impersonated user overwrites.
  realActorUserId: string | null;
  impersonationSessionId: string | null;
}

// Writing a tag as `undefined` is how a tag is *removed*: the scope keeps the key
// but the value is dropped when the event is serialized, so the next event carries
// no stale value.
const CLEARED_IDENTITY_TAGS = {
  active_hotel_chain_id: undefined,
  user_role_id: undefined,
  is_impersonating: undefined,
  real_actor_user_id: undefined,
  impersonation_session_id: undefined,
};

/** Overwrite the whole identity scope. */
export const applySentryIdentity = (identity: SentryIdentity): void => {
  const { userId, hotelChainId, roleId, isImpersonating, realActorUserId, impersonationSessionId } =
    identity;

  Sentry.setUser(userId ? { id: userId } : null);
  Sentry.setTags({
    ...CLEARED_IDENTITY_TAGS,
    active_hotel_chain_id: hotelChainId ?? undefined,
    user_role_id: roleId === null ? undefined : String(roleId),
    is_impersonating: String(isImpersonating),
    real_actor_user_id: realActorUserId ?? undefined,
    impersonation_session_id: impersonationSessionId ?? undefined,
  });
};

/** Drop the complete user/context scope — called on logout. */
export const clearSentryIdentity = (): void => {
  Sentry.setUser(null);
  Sentry.setTags(CLEARED_IDENTITY_TAGS);
};
